import React, { useEffect } from 'react'
import { graphql } from 'gatsby'
import styled from 'styled-components'
import loadable from '@loadable/component'

import SEO from '../components/SEO'
import Contact from '../components/contact/Contact'

const RichText = loadable(() => import('../components/RichText'))

const Text = ({
  location,
  data: {
    contentfulTextPage: {
      name,
      seoTitle,
      seoDescription,
      slug,
      content,
      html,
      friendbuyWidget,
      friendbuyWidgetId,
      formPhoneNumber,
      formName,
      formMessage,
      formEmail,
      send,
      contactUs,
      formFailedMessage,
      formSuccessMessage,
      sendingMessage,
      contactSubTitle,
      contactUsImage,
    },
  },
}) => {
  useEffect(() => {
    const customWidgetID = html?.html?.split('"')[1]

    if (customWidgetID) {
      const widgetElement = document.querySelector(`#${customWidgetID}`)

      if (widgetElement && window?.friendbuyAPI) {
        window.friendbuyAPI.push(['track', 'page', { name: slug }])
      }
    }
  }, [])

  return (
    <>
      <SEO
        location={location}
        title={seoTitle}
        description={seoDescription}
        pageCategory={'Text'}
        friendBuyWidgetId={friendbuyWidgetId}
        referrer={location.state?.referrer}
      />
      {contactUsImage ? (
        <Contact
          location={location}
          seoTitle={seoTitle}
          seoDescription={seoDescription}
          formPhoneNumber={formPhoneNumber}
          formName={formName}
          formMessage={formMessage}
          formEmail={formEmail}
          send={send}
          contactUs={contactUs}
          contactUsImage={contactUsImage}
          formFailedMessage={formFailedMessage}
          formSuccessMessage={formSuccessMessage}
          contactSubTitle={contactSubTitle}
          sendingMessage={sendingMessage}
        />
      ) : (
        <StyledDocument>
          <Title>{name}</Title>
          {content && (
            <Content>
              <RichText json={content}></RichText>
            </Content>
          )}
          {html?.html && (
            <div dangerouslySetInnerHTML={{ __html: html.html }} />
          )}
          {/*
          ClassName is required by friendbuy to activate;
          Widget id (e.g. d9R-pXW) comes from client;
          See SEO.js for how the code is integrated,
          and check with the client the widget is published;
          FriendBuy tracking is implemented in the Shopify checkout code
        */}
          {friendbuyWidget && friendbuyWidgetId && (
            <FriendBuy className={`friendbuy-${friendbuyWidgetId}`} />
          )}
        </StyledDocument>
      )}
    </>
  )
}

const StyledDocument = styled.article`
  padding: ${(props) => props.theme.space.marginMobile * 2}vw
    ${(props) => props.theme.space.marginMobile * 2}vw
    ${(props) => props.theme.space.marginMobile * 4}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding: ${(props) => props.theme.space.marginDesktop * 8}vw;
  }
`

const Title = styled.h1`
  color: #000;
  font-size: 9.25vw;
  line-height: 1.4;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: 6.25vw;
    text-align: center;
    letter-spacing: 0.02em;
  }
`

const Content = styled.div`
  line-height: 1.6;
  font-size: ${(props) => props.theme.fontSize.m23};
  margin-top: ${(props) => props.theme.space.marginMobile * 4}vw;

  ol,
  ul {
    list-style: inside;
    list-style-type: decimal;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: #111;
    margin: 8vw 0;
    font-size: ${(props) => props.theme.fontSize.m23};
    font-family: ${(props) => props.theme.fontFamily.bold};
    text-transform: uppercase;
  }

  p {
    margin: 4vw 0;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    max-width: 32vw;
    line-height: 2;
    font-size: ${(props) => props.theme.fontSize.d12};
    margin-top: ${(props) => props.theme.space.marginDesktop * 8}vw;
    margin-left: auto;
    margin-right: auto;

    ol,
    ul {
      list-style: inside;
      list-style-type: decimal;
      letter-spacing: 0.05em;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: #111;
      margin: 2vw 0;
      font-size: ${(props) => props.theme.fontSize.d12};
      letter-spacing: 0.05em;
      font-family: ${(props) => props.theme.fontFamily.bold};
    }

    p {
      margin: 1vw 0;
      letter-spacing: 0.05em;
    }
  }
`

const FriendBuy = styled.div`
  width: 100%;
  height: auto;
  margin-top: ${(props) => props.theme.space.marginMobile * 1.5}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin-top: ${(props) => props.theme.space.marginDesktop * 4}vw;
  }
`

export const TextQuery = graphql`
  query Text($id: String!) {
    contentfulTextPage(id: { eq: $id }) {
      name
      seoTitle
      seoDescription
      slug
      content {
        raw
      }
      html {
        html
      }
      friendbuyWidget
      friendbuyWidgetId
      formPhoneNumber
      formName
      formEmail
      formMessage
      send
      formFailedMessage
      formSuccessMessage
      contactSubTitle
      contactUs
      sendingMessage
      contactUsImage {
        title
        file {
          url
        }
        gatsbyImageData(
          layout: FULL_WIDTH
          sizes: "(max-width: 1023px) 53vw, 22vw"
        )
      }
    }
  }
`

export default Text
